import styled from "styled-components";

export const Page = styled.main`
  --bg: #1f1f1f;
  --bg-card: #2b2b2c;
  --bg-soft: rgba(255, 255, 255, 0.04);
  --border: rgba(255, 255, 255, 0.08);
  --text: #f4f4f5;
  --text-muted: rgba(244, 244, 245, 0.72);
  --text-soft: rgba(244, 244, 245, 0.52);
  --accent-start: #ff8a00;
  --accent-end: #f23d7f;

  position: relative;
  min-height: 100vh;
  overflow: hidden;
  background:
    radial-gradient(circle at 10% 0, rgba(242, 61, 127, 0.14), transparent 24%),
    radial-gradient(circle at 100% 0, rgba(255, 138, 0, 0.14), transparent 26%),
    linear-gradient(180deg, #202022 0%, #1a1a1a 100%);
  color: var(--text);
`;

export const Glow = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    radial-gradient(circle at 20% 16%, rgba(242, 61, 127, 0.14), transparent 20%),
    radial-gradient(circle at 82% 10%, rgba(255, 138, 0, 0.14), transparent 22%);
`;

export const Shell = styled.div`
  position: relative;
  z-index: 1;
  width: min(1120px, calc(100% - 40px));
  margin: 0 auto;
  padding: 24px 0 64px;

  @media (max-width: 768px) {
    width: min(100% - 28px, 1120px);
    padding-bottom: 40px;
  }
`;

export const TopBar = styled.header`
  position: sticky;
  top: 16px;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 18px 22px;
  border: 1px solid var(--border);
  border-radius: 24px;
  background: rgba(18, 18, 21, 0.72);
  backdrop-filter: blur(18px);

  @media (max-width: 860px) {
    flex-wrap: wrap;
  }
`;

export const BrandLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 14px;
  color: var(--text);
`;

export const BrandMark = styled.img`
  width: 132px;
  height: auto;
  object-fit: contain;

  @media (max-width: 640px) {
    width: 108px;
  }
`;

export const BrandCopy = styled.span`
  max-width: 270px;
  color: var(--text-soft);
  font-size: 0.92rem;
  line-height: 1.45;

  @media (max-width: 768px) {
    display: none;
  }
`;

export const NavLinks = styled.nav`
  display: inline-flex;
  flex-wrap: wrap;
  gap: 18px;

  @media (max-width: 860px) {
    width: 100%;
    order: 3;
  }
`;

export const NavAnchor = styled.a`
  color: var(--text-muted);
  font-size: 0.94rem;
  font-weight: 500;

  &:hover {
    color: var(--text);
  }
`;

export const TopButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 52px;
  padding: 0 22px;
  border-radius: 999px;
  background: linear-gradient(135deg, var(--accent-start), var(--accent-end));
  color: #fff;
  font-size: 0.95rem;
  font-weight: 700;
  box-shadow: 0 16px 32px rgba(242, 61, 127, 0.18);
`;

export const LoadingCard = styled.div`
  display: grid;
  place-items: center;
  min-height: 280px;
  margin-top: 32px;
  padding: 28px;
  border-radius: 28px;
  border: 1px solid var(--border);
  background: rgba(20, 20, 24, 0.86);
  color: var(--text-muted);
  font-size: 1.05rem;
`;

export const EmptyCard = styled.div`
  display: grid;
  gap: 14px;
  margin-top: 32px;
  padding: 28px;
  border-radius: 28px;
  border: 1px solid var(--border);
  background: rgba(20, 20, 24, 0.86);

  strong {
    font-size: 1.3rem;
  }

  span {
    color: var(--text-muted);
    line-height: 1.7;
  }
`;

export const CardGrid = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 0.94fr) minmax(340px, 0.98fr);
  gap: 22px;
  margin-top: 32px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

export const CardCover = styled.div`
  position: relative;
  min-height: 620px;
  border-radius: 28px;
  overflow: hidden;
  border: 1px solid var(--border);
  background: #151518;

  @media (max-width: 960px) {
    min-height: 420px;
  }
`;

export const CoverImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const CoverOverlay = styled.div`
  position: absolute;
  inset: 0;
  background:
    linear-gradient(180deg, rgba(20, 20, 24, 0.08), rgba(20, 20, 24, 0.42)),
    linear-gradient(180deg, rgba(20, 20, 24, 0) 36%, rgba(20, 20, 24, 0.46) 100%);
`;

export const CardBody = styled.article`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 22px;
  border-radius: 28px;
  background: var(--bg-card);
  border: 1px solid var(--border);
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.22);
`;

export const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

export const CompanyAvatar = styled.img`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  object-fit: cover;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
`;

export const CompanyMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const CardBadge = styled.span`
  display: inline-flex;
  width: fit-content;
  min-height: 30px;
  align-items: center;
  padding: 0 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.06);
  color: #f8d29a;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

export const CompanyName = styled.strong`
  font-size: 1rem;
  color: #fff;
`;

export const EventTitle = styled.h1`
  font-family: "Space Grotesk", sans-serif;
  font-size: clamp(1.8rem, 4vw, 2.7rem);
  line-height: 1.04;
  letter-spacing: -0.05em;
`;

export const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

export const EventMeta = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 32px;
  padding: 0 12px;
  border-radius: 999px;
  background: var(--bg-soft);
  color: rgba(244, 244, 245, 0.84);
  font-size: 0.84rem;
  font-weight: 600;
`;

export const InfoRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  color: rgba(244, 244, 245, 0.88);

  svg {
    margin-top: 3px;
    min-width: 14px;
    color: #f8d29a;
  }
`;

export const InfoText = styled.span`
  color: var(--text-muted);
  font-size: 0.98rem;
  line-height: 1.68;
`;

export const EventInfo = styled.p`
  color: var(--text-muted);
  font-size: 1rem;
  line-height: 1.72;
  white-space: pre-wrap;
`;

export const EventInfoLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  min-height: 46px;
  padding: 0 18px;
  border-radius: 14px;
  background: linear-gradient(135deg, var(--accent-start), var(--accent-end));
  color: #fff;
  font-size: 0.94rem;
  font-weight: 700;
  box-shadow: 0 14px 28px rgba(242, 61, 127, 0.16);
`;

export const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

export const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 30px;
  padding: 0 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: rgba(244, 244, 245, 0.82);
  font-size: 0.82rem;
  font-weight: 500;
`;

export const SummaryText = styled.p`
  margin-top: auto;
  color: var(--text-muted);
  font-size: 0.98rem;
  line-height: 1.72;
`;

export const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 8px;
`;

export const ActionButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 14px;
  min-height: 58px;
  padding: 0 18px;
  border-radius: 18px;
  background: linear-gradient(135deg, var(--accent-start), var(--accent-end));
  color: #fff;
  font-weight: 700;
  box-shadow: 0 16px 32px rgba(242, 61, 127, 0.18);
`;

export const SecondaryButton = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 14px;
  min-height: 58px;
  padding: 0 18px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: var(--text);
  opacity: 0.78;
`;

export const ActionIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  min-width: 40px;
  height: 40px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.14);
  font-size: 1.1rem;
`;

export const ActionText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const ActionHint = styled.span`
  font-size: 0.76rem;
  opacity: 0.82;
`;

export const ActionTitle = styled.span`
  font-size: 0.96rem;
  font-weight: 700;
`;

export const Footer = styled.footer`
  display: flex;
  justify-content: space-between;
  gap: 18px;
  padding: 24px 0 0;
  color: var(--text-soft);
  font-size: 0.92rem;

  @media (max-width: 720px) {
    flex-direction: column;
  }
`;

export const FooterLink = styled.a`
  color: var(--text-muted);

  &:hover {
    color: var(--text);
  }
`;
